import {
  BottomSheet,
  type BottomSheetHandle,
} from "@-/dev.improve.haniverse.components.ui.bottom-sheet";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Surface } from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import {
  morphemeDetailsTileSliceQueryFn,
  useGetMorphemeDetailsTileSliceQueryKey,
} from "@-/dev.improve.haniverse.lib.morpheme.read";
import { MorphemeDescriptionRenderer } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.description";
import {
  LevelSurfaceBody,
  LevelSurfaceHeader,
} from "@-/dev.improve.haniverse.routes.nav.app._components.level.surface";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { ActivityIndicator, View } from "@-/com.npmjs.react-native";
import { queryOptions, useQueries } from "@-/com.npmjs.tanstack__react-query";
import type { Ref } from "react";
import { getLevelDetailsNavigation } from "#get-level-details-navigation";
import type { LevelDetailsSheetGate } from "#get-level-details-sheet-gate";
import { getLevelsListEntryLabel } from "#levels-list-entry";

function cardGloss(card: MorphemeDetailsTileSlice | null): string {
  return card?.back?.trim() || card?.pinyinList[0]?.gloss?.trim() || "No meaning available yet.";
}

function LevelMnemonicRow({
  card,
  character,
  isLast,
  isPending,
}: {
  card: MorphemeDetailsTileSlice | null;
  character: string;
  isLast: boolean;
  isPending: boolean;
}) {
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const description = card?.description?.trim() ?? "";
  const gloss = cardGloss(card);

  return (
    <View
      accessibilityLabel={`${character}. ${gloss}. ${
        description || (isPending ? "Loading mnemonic." : "No mnemonic authored yet.")
      }`}
      accessible
      className={`flex-row items-start gap-3 py-4 ${isLast ? "" : "border-b border-border/60"}`}
    >
      <View className="size-17 shrink-0 items-center justify-center rounded-xs border border-border bg-muted/40">
        <GraphemeFontGlyph
          className="text-[42px] leading-13 text-foreground"
          glyph={getGraphemeGlyph(character, { uncolored: true })}
        />
      </View>
      <View className="min-w-0 flex-1 gap-1.5 pt-0.5">
        <Text className="text-[19px] leading-6 font-bold text-foreground">{gloss}</Text>
        {description ? (
          <MorphemeDescriptionRenderer className="pr-1" content={description} />
        ) : isPending ? (
          <View className="flex-row items-center gap-2 py-1">
            <ActivityIndicator size="small" />
          </View>
        ) : (
          <Text className="text-[14px] leading-5 text-muted-foreground italic">
            No mnemonic authored yet.
          </Text>
        )}
      </View>
    </View>
  );
}

function LevelDetailsNavigation({
  activeLevelPosition,
  levelCount,
  onNavigate,
}: {
  activeLevelPosition: number;
  levelCount: number;
  onNavigate: (position: number) => void;
}) {
  const { nextPosition, previousPosition } = getLevelDetailsNavigation(
    activeLevelPosition,
    levelCount,
  );

  return (
    <View className="shrink-0 flex-row items-center gap-1">
      <Button
        accessibilityLabel="Previous level"
        className="size-11"
        disabled={previousPosition === null}
        onPress={() => {
          if (previousPosition !== null) {
            onNavigate(previousPosition);
          }
        }}
        size="fill"
        testID="level-details-previous-button"
        variant="ghost"
      >
        <Icon className="text-muted-foreground" name="MdChevronLeft" size={22} />
      </Button>
      <Button
        accessibilityLabel="Next level"
        className="size-11"
        disabled={nextPosition === null}
        onPress={() => {
          if (nextPosition !== null) {
            onNavigate(nextPosition);
          }
        }}
        size="fill"
        testID="level-details-next-button"
        variant="ghost"
      >
        <Icon className="text-muted-foreground" name="MdChevronRight" size={22} />
      </Button>
    </View>
  );
}

export function LevelDetailsSheet({
  gate,
  onNavigate,
  ref,
}: {
  gate: LevelDetailsSheetGate;
  onNavigate: (position: number) => void;
  ref?: Ref<BottomSheetHandle>;
}) {
  const { activeLevelPosition, indexedLevel, levelCount } = gate;
  const characters = indexedLevel.level.characters;
  const getMorphemeDetailsTileSliceQueryKey = useGetMorphemeDetailsTileSliceQueryKey();
  const cardResults = useQueries({
    queries: characters.map((morpheme) =>
      queryOptions({
        queryFn: morphemeDetailsTileSliceQueryFn,
        queryKey: getMorphemeDetailsTileSliceQueryKey({ morpheme }),
      }),
    ),
  });

  const { level } = indexedLevel;
  const cardCount = arrayLength(characters);

  return (
    <BottomSheet ref={ref} scrollable>
      {/* Offset 12px of the sheet's 16px content inset, leaving 4px at each edge. */}
      <Surface className="-mx-3" testID="level-details-surface">
        <LevelSurfaceHeader
          label={getLevelsListEntryLabel(indexedLevel)}
          level={level}
          trailing={
            <LevelDetailsNavigation
              activeLevelPosition={activeLevelPosition}
              levelCount={levelCount}
              onNavigate={onNavigate}
            />
          }
        />
        <LevelSurfaceBody className="px-3">
          {characters.map((character, index) => {
            const cardResult = cardResults[index];
            return (
              <LevelMnemonicRow
                card={cardResult?.data ?? null}
                character={character}
                isLast={index === cardCount - 1}
                isPending={cardResult?.isPending === true}
                key={`${character}-${index}`}
              />
            );
          })}
        </LevelSurfaceBody>
      </Surface>
    </BottomSheet>
  );
}
